import React, { useState } from 'react';
import axios from 'axios';
import { Search, Package, MapPin, User, Calendar, CheckCircle, Clock, Truck, AlertCircle, XCircle, Crown, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API, toast } from '@/App';

const TrackingSearch = () => {
  const [trackingCode, setTrackingCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [error, setError] = useState('');

  const statusConfig = {
    pending_payment: {
      label: 'En attente de paiement',
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      borderColor: 'border-amber-300'
    },
    paid: {
      label: 'Payé',
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-300'
    },
    awaiting_assignment: {
      label: 'En attente d\'assignation',
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-300'
    },
    pickup_in_progress: {
      label: 'Ramassage en cours',
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-300'
    },
    picked_up: {
      label: 'Colis récupéré',
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-300'
    },
    in_transit: {
      label: 'En transit',
      icon: Truck,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-300'
    },
    at_relay: {
      label: 'Au point relais',
      icon: MapPin,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
      borderColor: 'border-cyan-300'
    },
    out_for_delivery: {
      label: 'En cours de livraison',
      icon: Truck,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-300'
    },
    delivered: {
      label: 'Livré',
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-300'
    },
    cancelled: {
      label: 'Annulé',
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-300'
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!trackingCode.trim()) return;

    setLoading(true);
    setError('');
    setDelivery(null);

    try {
      const response = await axios.get(`${API}/track/${trackingCode.trim()}`);
      setDelivery(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Code de suivi invalide. Veuillez vérifier et réessayer.');
      } else {
        setError('Erreur lors de la récupération des informations. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrentStatusConfig = () => {
    if (!delivery) return null;
    return statusConfig[delivery.status] || statusConfig.in_transit;
  };

  const getServiceTier = (item) => {
    if (!item) return 'standard';
    if (item.service_tier) return item.service_tier;
    return item.is_express ? 'express' : 'standard';
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-cyan-600" />
            Suivi de colis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              type="text"
              placeholder="Entrez votre code de suivi (ex: CN1234567890)"
              value={trackingCode}
              onChange={(e) => setTrackingCode(e.target.value.toUpperCase())}
              className="flex-1"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={loading || !trackingCode.trim()}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              {loading ? 'Recherche...' : 'Suivre'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Card className="border-2 border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-900">Code de suivi introuvable</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery Information */}
      {delivery && (
        <div className="space-y-6">
          {/* Status Card */}
          <Card className={`border-2 ${getCurrentStatusConfig()?.borderColor}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {React.createElement(getCurrentStatusConfig()?.icon, {
                    className: `w-10 h-10 ${getCurrentStatusConfig()?.color}`
                  })}
                  <div>
                    <p className="text-sm text-gray-600">Statut actuel</p>
                    <p className="text-xl font-bold text-gray-900">
                      {getCurrentStatusConfig()?.label}
                    </p>
                  </div>
                </div>
                {(() => {
                  const serviceTier = getServiceTier(delivery);
                  const isExpressTier = serviceTier === 'express';
                  const isInsured = Boolean(delivery.is_insured);
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      {!isExpressTier && <Badge variant="outline">Service Standard</Badge>}
                      {isExpressTier && (
                        <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1 flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          EXPRESS
                        </Badge>
                      )}
                      {isInsured && (
                        <Badge className="bg-cyan-600 text-white px-3 py-1 flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" />
                          Assurance
                        </Badge>
                      )}
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Delivery Details Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Package Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4 text-red-600" />
                  Informations du colis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-600">Code de suivi</p>
                  <p className="font-semibold">{delivery.tracking_code}</p>
                </div>
                <div>
                  <p className="text-gray-600">Poids</p>
                  <p className="font-semibold">{delivery.weight} kg</p>
                </div>
                <div>
                  <p className="text-gray-600">Description</p>
                  <p className="font-semibold">{delivery.description}</p>
                </div>
                <div>
                  <p className="text-gray-600">Coût</p>
                  <p className="font-semibold">{delivery.cost} {delivery.currency}</p>
                {(delivery.transport_amount !== undefined || delivery.express_surcharge_amount || delivery.insurance_amount) && (
                  <div className="border-t pt-2 mt-2 space-y-1">
                    <p className="text-xs">Transport: {Number(delivery.transport_amount ?? delivery.cost ?? 0).toLocaleString()} {delivery.currency}</p>
                    <p className="text-xs">Express: + {Number(delivery.express_surcharge_amount ?? 0).toLocaleString()} {delivery.currency}</p>
                    <p className="text-xs">Assurance: + {Number(delivery.insurance_amount ?? 0).toLocaleString()} {delivery.currency}</p>
                  </div>
                )}
                </div>
              </CardContent>
            </Card>

            {/* Addresses */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-600" />
                  Adresses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">Ramassage</p>
                  <p className="font-semibold">{delivery.pickup_address}</p>
                  <p className="text-gray-600">{delivery.pickup_district}, {delivery.pickup_city}</p>
                </div>
                <div className="border-t pt-2">
                  <p className="text-gray-600 mb-1">Livraison</p>
                  <p className="font-semibold">{delivery.delivery_address}</p>
                  <p className="text-gray-600">{delivery.delivery_district}, {delivery.delivery_city}</p>
                </div>
              </CardContent>
            </Card>

            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-green-600" />
                  Destinataire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-600">Nom</p>
                  <p className="font-semibold">{delivery.client_info?.full_name}</p>
                </div>
                <div>
                  <p className="text-gray-600">Téléphone</p>
                  <p className="font-semibold">{delivery.client_info?.phone}</p>
                </div>
              </CardContent>
            </Card>

            {/* Livreur Info */}
            {delivery.livreur_name && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    Livreur assigné
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <p className="text-gray-600">Nom du livreur</p>
                    <p className="font-semibold">{delivery.livreur_name}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                Historique de suivi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Timeline Items */}
                <div className="space-y-4">
                  {delivery.tracking_history?.map((event, index) => {
                    const config = statusConfig[event.status] || statusConfig.in_transit;
                    const Icon = config.icon;
                    const isLast = index === delivery.tracking_history.length - 1;

                    return (
                      <div key={index} className="relative flex gap-3">
                        {/* Icon */}
                        <div className={`
                          relative z-10 w-10 h-10 rounded-full flex items-center justify-center
                          ${config.bgColor} ${config.borderColor} border-2
                        `}>
                          <Icon className={`w-5 h-5 ${config.color}`} />
                        </div>

                        {/* Content */}
                        <div className={`flex-1 pb-4 ${!isLast ? 'border-b' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">
                                {config.label}
                              </p>
                              {event.note && (
                                <p className="text-xs text-gray-600 mt-1">
                                  {event.note}
                                </p>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {formatDate(event.timestamp)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TrackingSearch;
